import React, { useEffect, useMemo, useState } from "react";
import { hotelApi } from "./hotelApi";

const emptyForm = {
  id: 0,
  full_name: "",
  phone: "",
  email: "",
  id_number: "",
  address: "",
  nationality: "",
};

export default function HotelGuestsScreen({ onBack }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const showMsg = (m) => {
    setMsg(m);
    clearTimeout(showMsg._t);
    showMsg._t = setTimeout(() => setMsg(""), 4000);
  };

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;

    return (rows || []).filter((g) => {
      return (
        String(g.full_name || "").toLowerCase().includes(q) ||
        String(g.phone || "").toLowerCase().includes(q) ||
        String(g.email || "").toLowerCase().includes(q) ||
        String(g.id_number || "").toLowerCase().includes(q) ||
        String(g.address || "").toLowerCase().includes(q) ||
        String(g.nationality || "").toLowerCase().includes(q)
      );
    });
  }, [rows, search]);

  const loadGuests = async () => {
    try {
      setLoading(true);
      const res = await hotelApi.get("/guests");
      const list = Array.isArray(res.data?.rows) ? res.data.rows : [];
      setRows(list);
    } catch (err) {
      console.error(err);
      const serverMsg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        "Failed to load guests.";
      showMsg(`❌ ${serverMsg}`);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGuests();
  }, []);

  const resetForm = () => {
    setForm(emptyForm);
  };

  const openAddForm = () => {
    resetForm();
    setShowForm(true);
  };

  const closeForm = () => {
    resetForm();
    setShowForm(false);
  };

  const openEditForm = (guest) => {
    setForm({
      id: Number(guest.id || 0),
      full_name: String(guest.full_name || ""),
      phone: String(guest.phone || ""),
      email: String(guest.email || ""),
      id_number: String(guest.id_number || ""),
      address: String(guest.address || ""),
      nationality: String(guest.nationality || ""),
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const saveGuest = async (e) => {
    if (e?.preventDefault) e.preventDefault();

    const payload = {
      ...form,
      full_name: String(form.full_name || "").trim(),
      phone: String(form.phone || "").trim(),
      email: String(form.email || "").trim(),
      id_number: String(form.id_number || "").trim(),
      address: String(form.address || "").trim(),
      nationality: String(form.nationality || "").trim(),
    };

    if (!payload.full_name) {
      showMsg("❌ Guest name is required.");
      return;
    }

    try {
      setSaving(true);

      if (payload.id) {
        await hotelApi.put(`/guests/${payload.id}`, payload);
        showMsg("✅ Guest updated.");
      } else {
        await hotelApi.post("/guests", payload);
        showMsg("✅ Guest created.");
      }

      closeForm();
      await loadGuests();
    } catch (err) {
      console.error(err);
      const serverMsg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        `Save failed (${err?.response?.status || "no status"})`;
      showMsg(`❌ ${serverMsg}`);
    } finally {
      setSaving(false);
    }
  };

  const deleteGuest = async (id, full_name) => {
    const ok = window.confirm(
      `Delete guest ${full_name || ""}? This cannot be undone.`
    );
    if (!ok) return;

    try {
      setLoading(true);
      await hotelApi.delete(`/guests/${id}`);
      showMsg("🗑 Guest deleted.");
      await loadGuests();
    } catch (err) {
      console.error(err);
      const serverMsg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        `Delete failed (${err?.response?.status || "no status"})`;
      showMsg(`❌ ${serverMsg}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.topBar}>
        <button style={styles.backTopBtn} onClick={onBack}>
          ◀ Back to Menu
        </button>
        <div style={styles.pageLabel}>Page: hotel-guests</div>
      </div>

      <div style={styles.container}>
        <div style={styles.headerRow}>
          <div>
            <h1 style={styles.title}>Hotel Guests</h1>
            <p style={styles.subtitle}>Create and manage hotel guest records.</p>
          </div>

          <div style={styles.headerActions}>
            <button style={styles.secondaryBtn} onClick={loadGuests} disabled={loading}>
              ↻ Refresh
            </button>
            <button style={styles.primaryBtn} onClick={openAddForm}>
              + Add Guest
            </button>
          </div>
        </div>

        {msg ? <div style={styles.alert}>{msg}</div> : null}

        <div style={styles.filtersCard}>
          <div style={styles.filtersGrid}>
            <div>
              <label style={styles.label}>Search</label>
              <input
                style={styles.input}
                placeholder="name, phone, email, id number..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <div>
              <label style={styles.label}>Guests</label>
              <div style={styles.helperText}>
                {filteredRows.length} guest(s)
              </div>
            </div>
          </div>
        </div>

        {showForm && (
          <div style={styles.formCard}>
            <div style={styles.cardHeader}>
              <h3 style={styles.cardTitle}>{form.id ? "Edit Guest" : "Add Guest"}</h3>
            </div>

            <form onSubmit={saveGuest}>
              <div style={styles.formGrid}>
                <div>
                  <label style={styles.label}>Full Name *</label>
                  <input
                    style={styles.input}
                    value={form.full_name}
                    onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                    placeholder="e.g. John Doe"
                  />
                </div>

                <div>
                  <label style={styles.label}>Phone</label>
                  <input
                    style={styles.input}
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="e.g. 0712345678"
                  />
                </div>

                <div>
                  <label style={styles.label}>Email</label>
                  <input
                    style={styles.input}
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="e.g. guest@email.com"
                  />
                </div>

                <div>
                  <label style={styles.label}>ID Number / Passport</label>
                  <input
                    style={styles.input}
                    value={form.id_number}
                    onChange={(e) => setForm({ ...form, id_number: e.target.value })}
                    placeholder="e.g. A1234567"
                  />
                </div>

                <div>
                  <label style={styles.label}>Address</label>
                  <input
                    style={styles.input}
                    value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                    placeholder="e.g. Nairobi, Kenya"
                  />
                </div>

                <div>
                  <label style={styles.label}>Nationality</label>
                  <input
                    style={styles.input}
                    value={form.nationality}
                    onChange={(e) => setForm({ ...form, nationality: e.target.value })}
                    placeholder="e.g. Kenyan"
                  />
                </div>
              </div>

              <div style={styles.formActions}>
                <button
                  type="button"
                  style={styles.secondaryBtn}
                  onClick={closeForm}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button type="submit" style={styles.primaryBtn} disabled={saving}>
                  {saving ? "Saving..." : form.id ? "Update Guest" : "Save Guest"}
                </button>
              </div>
            </form>
          </div>
        )}

        <div style={styles.tableCard}>
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>Guests</h3>
            <div style={styles.roomCount}>{filteredRows.length} guest(s)</div>
          </div>

          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>ID</th>
                  <th style={styles.th}>Name</th>
                  <th style={styles.th}>Phone</th>
                  <th style={styles.th}>Email</th>
                  <th style={styles.th}>ID Number</th>
                  <th style={styles.th}>Address</th>
                  <th style={styles.th}>Nationality</th>
                  <th style={styles.th}>Action</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td style={styles.emptyTd} colSpan="8">
                      Loading guests...
                    </td>
                  </tr>
                ) : filteredRows.length === 0 ? (
                  <tr>
                    <td style={styles.emptyTd} colSpan="8">
                      No guests found. Click Add Guest to create the first guest.
                    </td>
                  </tr>
                ) : (
                  filteredRows.map((guest) => (
                    <tr key={guest.id}>
                      <td style={styles.td}>{guest.id}</td>
                      <td style={styles.td}>{guest.full_name || "—"}</td>
                      <td style={styles.td}>{guest.phone || "—"}</td>
                      <td style={styles.td}>{guest.email || "—"}</td>
                      <td style={styles.td}>{guest.id_number || "—"}</td>
                      <td style={styles.td}>{guest.address || "—"}</td>
                      <td style={styles.td}>{guest.nationality || "—"}</td>
                      <td style={styles.td}>
                        <div style={styles.actionRow}>
                          <button
                            style={styles.editBtn}
                            onClick={() => openEditForm(guest)}
                          >
                            Edit
                          </button>
                          <button
                            style={styles.deleteBtn}
                            onClick={() => deleteGuest(guest.id, guest.full_name)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div style={styles.bottomRow}>
          <button style={styles.secondaryBtn} onClick={onBack}>
            ◀ Back
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background: "#f5f7fb",
    padding: 18,
    boxSizing: "border-box",
    fontFamily: "Arial, sans-serif",
    color: "#14213d",
  },
  topBar: {
    background: "#f7f9fc",
    border: "1px solid #dfe5ee",
    borderRadius: 18,
    padding: "10px 12px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
  },
  pageLabel: {
    fontSize: 14,
    color: "#4b5563",
    fontWeight: 700,
  },
  backTopBtn: {
    background: "#2563eb",
    color: "#fff",
    border: "none",
    borderRadius: 12,
    padding: "10px 16px",
    fontWeight: 700,
    cursor: "pointer",
  },
  container: {
    background: "#f8fafc",
    border: "1px solid #dfe5ee",
    borderRadius: 24,
    padding: 18,
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 16,
    flexWrap: "wrap",
    marginBottom: 14,
  },
  title: {
    margin: 0,
    fontSize: 22,
    fontWeight: 800,
  },
  subtitle: {
    margin: "6px 0 0 0",
    fontSize: 14,
    color: "#475569",
    fontWeight: 600,
  },
  headerActions: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
  },
  primaryBtn: {
    background: "#2563eb",
    color: "#fff",
    border: "none",
    borderRadius: 12,
    padding: "11px 16px",
    fontWeight: 700,
    cursor: "pointer",
    minWidth: 120,
  },
  secondaryBtn: {
    background: "#fff",
    color: "#111827",
    border: "1px solid #d1d5db",
    borderRadius: 12,
    padding: "11px 16px",
    fontWeight: 700,
    cursor: "pointer",
    minWidth: 110,
  },
  alert: {
    marginBottom: 14,
    padding: "12px 14px",
    background: "#eef4ff",
    border: "1px solid #c7d7fe",
    borderRadius: 12,
    fontWeight: 700,
  },
  filtersCard: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 18,
    padding: 14,
    marginBottom: 16,
  },
  filtersGrid: {
    display: "grid",
    gridTemplateColumns: "1.5fr 1fr",
    gap: 14,
  },
  formCard: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 18,
    padding: 16,
    marginBottom: 16,
  },
  tableCard: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 18,
    overflow: "hidden",
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "14px 14px 10px 14px",
    borderBottom: "1px solid #eef2f7",
  },
  cardTitle: {
    margin: 0,
    fontSize: 16,
    fontWeight: 800,
  },
  roomCount: {
    fontSize: 13,
    color: "#475569",
    fontWeight: 700,
  },
  label: {
    display: "block",
    marginBottom: 6,
    fontSize: 14,
    fontWeight: 700,
  },
  helperText: {
    minHeight: 44,
    display: "flex",
    alignItems: "center",
    color: "#475569",
    fontSize: 13,
    fontWeight: 600,
  },
  input: {
    width: "100%",
    boxSizing: "border-box",
    padding: "11px 12px",
    borderRadius: 12,
    border: "1px solid #d1d5db",
    outline: "none",
    fontSize: 14,
    background: "#fff",
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 14,
  },
  formActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    marginTop: 16,
    flexWrap: "wrap",
  },
  tableWrap: {
    width: "100%",
    overflowX: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: 1100,
  },
  th: {
    textAlign: "left",
    padding: "12px 14px",
    fontSize: 13,
    fontWeight: 800,
    color: "#0f172a",
    background: "#f8fafc",
    borderBottom: "1px solid #e5e7eb",
  },
  td: {
    padding: "12px 14px",
    fontSize: 14,
    borderBottom: "1px solid #eef2f7",
    verticalAlign: "middle",
  },
  emptyTd: {
    padding: "18px 14px",
    fontSize: 15,
    color: "#475569",
    fontWeight: 600,
  },
  actionRow: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
  },
  editBtn: {
    background: "#fff",
    color: "#111827",
    border: "1px solid #d1d5db",
    borderRadius: 10,
    padding: "8px 12px",
    cursor: "pointer",
    fontWeight: 700,
  },
  deleteBtn: {
    background: "#fff5f5",
    color: "#b91c1c",
    border: "1px solid #ef4444",
    borderRadius: 10,
    padding: "8px 12px",
    cursor: "pointer",
    fontWeight: 700,
  },
  bottomRow: {
    marginTop: 14,
  },
};